import { API_BASE_URL } from '../config';
class SessionManager {
    constructor() {
        this.SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes
        this.REFRESH_INTERVAL = 30 * 1000; // Refresh every 30 seconds
        this.refreshTimer = null;
        this.activityTimer = null;
        this.lastActivity = Date.now();
        this.isActive = true;
        
        this.setupActivityTracking();
        this.startSessionRefresh();
    }

    setupActivityTracking() {
        // Track user activity
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        
        const updateActivity = () => {
            this.lastActivity = Date.now();
            this.isActive = true;
        };

        events.forEach(event => {
            document.addEventListener(event, updateActivity, true);
        });

        // Check for inactivity every minute
        setInterval(() => {
            const now = Date.now();
            const timeSinceActivity = now - this.lastActivity;
            
            if (timeSinceActivity > this.SESSION_TIMEOUT) {
                this.handleSessionExpired();
            }
        }, 60000); // Check every minute
    }

    startSessionRefresh() {
        this.refreshTimer = setInterval(async () => {
            if (this.isActive) {
                await this.refreshSession();
            }
        }, this.REFRESH_INTERVAL);
    }

    async refreshSession() {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                return;
            }

            const response = await fetch(`${API_BASE_URL}/session/refresh`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                this.lastActivity = Date.now();
            } else {
                const errorData = await response.json();
                if (errorData.code === 'SESSION_EXPIRED' || errorData.code === 'TOKEN_EXPIRED') {
                    this.handleSessionExpired();
                }
            }
        } catch (error) {
            console.error('Session refresh failed:', error);
            // Don't immediately expire on network errors
        }
    }

    async checkSessionStatus() {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                return { valid: false, reason: 'NO_TOKEN' };
            }

            const response = await fetch(`${API_BASE_URL}/session/status`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                return { 
                    valid: true, 
                    timeRemaining: data.timeRemaining,
                    lastActivity: data.lastActivity
                };
            } else {
                const errorData = await response.json();
                return { 
                    valid: false, 
                    reason: errorData.code || 'UNKNOWN_ERROR',
                    message: errorData.message
                };
            }
        } catch (error) {
            console.error('Session status check failed:', error);
            return { valid: false, reason: 'NETWORK_ERROR' };
        }
    }

    async logout() {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                await fetch(`${API_BASE_URL}/session/logout`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            }
        } catch (error) {
            console.error('Logout request failed:', error);
        } finally {
            this.clearSession();
        }
    }

    clearSession() {
        // Clear timers
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }

        // Clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Reset state
        this.isActive = false;
        this.lastActivity = 0;

        // Redirect to login
        window.location.href = '/login';
    }

    handleSessionExpired() {
        this.clearSession();
        
        // Show notification
        if (window.showSessionExpiredNotification) {
            window.showSessionExpiredNotification();
        } else {
            alert('Your session has expired due to inactivity. Please log in again.');
        }
    }

    // Method to manually extend session (called on user activity)
    extendSession() {
        this.lastActivity = Date.now();
        this.isActive = true;
    }

    // Method to pause session management (useful for modals, etc.)
    pauseSession() {
        this.isActive = false;
    }

    // Method to resume session management
    resumeSession() {
        this.isActive = true;
        this.lastActivity = Date.now();
    }

    // Get time remaining in session
    getTimeRemaining() {
        const now = Date.now();
        const timeSinceActivity = now - this.lastActivity;
        return Math.max(0, this.SESSION_TIMEOUT - timeSinceActivity);
    }
}

// Create global instance
const sessionManager = new SessionManager();

// Export for use in components
export default sessionManager;

// Also make it available globally for debugging
window.sessionManager = sessionManager;
